/**
 * src/components/pipeline/quote/copilot/CopilotBits.jsx — TICKET-872.
 * Presentational pieces of the Quote Copilot panel: owner chip, per-turn change
 * log (with Revert this turn), and the Open items list grouped by owner.
 */

import { useState } from 'react';
import { User, Wrench, Crown, Undo2, Copy, FileText, AlertTriangle } from 'lucide-react';
import { useT } from '../../../../lib/i18n/I18nContext';
import { changeLogText, raisedOpenItems, summarizeTurnChanges } from './copilotChanges';

/** Owner -> icon + token pair. Text token on its own tint (components/CLAUDE.md colour pairs). */
export const OWNERS = Object.freeze({
  customer: { Icon: User, bg: 'var(--accent-bg)', color: 'var(--accent)' },
  internal: { Icon: Wrench, bg: 'var(--warn-tint)', color: 'var(--warn)' },
  ceo: { Icon: Crown, bg: 'var(--info-tint)', color: 'var(--info)' },
});
export const OWNER_ORDER = Object.freeze(['customer', 'internal', 'ceo']);

const smallBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 44, padding: '8px 12px',
  borderRadius: 10, border: 'none', outline: '1px solid var(--bdr)', background: 'var(--card)',
  color: 'var(--t2)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
};

export function OwnerChip({ owner }) {
  const { t } = useT();
  const key = OWNERS[owner] ? owner : 'internal';
  const { Icon, bg, color } = OWNERS[key];
  return (
    <span data-testid={`copilot-owner-chip-${key}`} style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', borderRadius: 6,
      fontSize: 11, fontWeight: 600, background: bg, color, flexShrink: 0,
    }}>
      <Icon size={12} strokeWidth={2} aria-hidden="true" />
      {t(`quoteCopilot.owner.${key}`)}
    </span>
  );
}

/**
 * Beneath an assistant turn: the change log, Revert this turn (two taps — a
 * revert restores the quote to BEFORE this turn, so it also undoes any later
 * line edits), and the open items the turn raised.
 */
export function TurnChangeLog({ msg, reverted, busy, onRevert }) {
  const { t } = useT();
  const [confirming, setConfirming] = useState(false);
  const { counts, items, wrote } = summarizeTurnChanges(msg.toolCalls);
  const raised = raisedOpenItems(msg.toolCalls);
  if (!wrote) return null;
  return (
    <div data-testid="copilot-turn-changes" style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
      <div style={{ padding: '8px 10px', borderRadius: 8, background: 'var(--accent-bg)', display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span data-testid="copilot-changelog" style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent)' }}>
          {changeLogText(counts, t)}
        </span>
        <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: 'var(--t2)' }}>
          {items.filter((ch) => ch.kind !== 'open_item').map((ch) => (
            <li key={`${ch.kind}-${ch.id}`} data-testid="copilot-change-item">
              {ch.label}
              {ch.review_required && (
                <span style={{ color: 'var(--warn)', marginLeft: 6 }}>
                  <AlertTriangle size={11} aria-hidden="true" /> {t('quoteCopilot.needsReview')}
                </span>
              )}
            </li>
          ))}
        </ul>
        {msg.revisionId && (reverted ? (
          <span data-testid="copilot-reverted" style={{ fontSize: 12, color: 'var(--tm)' }}>{t('quoteCopilot.reverted')}</span>
        ) : confirming ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 12, color: 'var(--t2)' }}>{t('quoteCopilot.revertWarning')}</span>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button type="button" data-testid="copilot-revert-confirm" disabled={busy}
                onClick={() => { setConfirming(false); onRevert(msg.id); }}
                style={{ ...smallBtn, outline: 'none', background: 'var(--bad-tint)', color: 'var(--bad)', opacity: busy ? 0.5 : 1, cursor: busy ? 'default' : 'pointer' }}>
                <Undo2 size={14} aria-hidden="true" /> {t('quoteCopilot.revertConfirm')}
              </button>
              <button type="button" data-testid="copilot-revert-cancel" onClick={() => setConfirming(false)} style={smallBtn}>
                {t('common.cancel')}
              </button>
            </div>
          </div>
        ) : (
          <button type="button" data-testid="copilot-revert" disabled={busy} onClick={() => setConfirming(true)}
            style={{ ...smallBtn, alignSelf: 'flex-start', opacity: busy ? 0.5 : 1, cursor: busy ? 'default' : 'pointer' }}>
            <Undo2 size={14} aria-hidden="true" /> {t('quoteCopilot.revert')}
          </button>
        ))}
      </div>
      {raised.map((it) => (
        <div key={it.id} data-testid="copilot-turn-open-item" style={{
          padding: '8px 10px', borderRadius: 8, background: 'var(--glass)', border: '1px solid var(--bdr)',
          display: 'flex', alignItems: 'flex-start', gap: 8,
        }}>
          <OwnerChip owner={it.owner} />
          <span style={{ fontSize: 12, color: 'var(--t1)', flex: 1 }}>{it.question}</span>
        </div>
      ))}
    </div>
  );
}

/** Open items tab: grouped customer -> internal -> ceo. Customer questions copy as a list for an email. */
export function OpenItemsList({ items, error, onCopy }) {
  const { t } = useT();
  if (error) {
    return <div data-testid="copilot-items-error" style={{ padding: 16, fontSize: 13, color: 'var(--bad)' }}>{t('quoteCopilot.loadFailed')}</div>;
  }
  if (!items.length) {
    return (
      <div data-testid="copilot-items-empty" style={{ textAlign: 'center', padding: '32px 16px', color: 'var(--tm)' }}>
        <FileText size={24} strokeWidth={1.5} style={{ opacity: 0.4, marginBottom: 8 }} aria-hidden="true" />
        <div style={{ fontSize: 13 }}>{t('quoteCopilot.noOpenItems')}</div>
      </div>
    );
  }
  const groups = OWNER_ORDER
    .map((owner) => ({ owner, rows: items.filter((it) => it.owner === owner) }))
    .filter((g) => g.rows.length);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {groups.map(({ owner, rows }) => (
        <section key={owner} data-testid={`copilot-items-group-${owner}`}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--t2)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {t(`quoteCopilot.owner.${owner}`)} ({rows.length})
            </span>
            {owner === 'customer' && (
              <button type="button" data-testid="copilot-items-copy-customer" style={smallBtn}
                onClick={() => onCopy(rows.filter((r) => r.status === 'open').map((r) => `- ${r.question}`).join('\n'))}>
                <Copy size={14} aria-hidden="true" /> {t('quoteCopilot.copyForEmail')}
              </button>
            )}
          </div>
          {rows.map((it) => (
            <div key={it.id} data-testid="copilot-open-item-row" style={{
              padding: '10px 0', borderBottom: '1px solid var(--bdr)', display: 'flex', flexDirection: 'column', gap: 6,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <OwnerChip owner={it.owner} />
                <span style={{ fontSize: 11, color: 'var(--tm)', fontWeight: 500 }}>{t(`quoteCopilot.status.${it.status}`)}</span>
              </div>
              <p style={{ margin: 0, fontSize: 13, color: 'var(--t1)', fontWeight: 500, lineHeight: 1.4 }}>{it.question}</p>
              {it.answer && (
                <p style={{ margin: 0, fontSize: 12, color: 'var(--t2)', lineHeight: 1.4, paddingLeft: 8, borderLeft: '2px solid var(--accent)' }}>
                  {it.answer}
                </p>
              )}
            </div>
          ))}
        </section>
      ))}
    </div>
  );
}
